import React, { useState } from 'react';
import './HolisticTestimonialsManagement.css';

/**
 * 🎯 HOLISTIC TESTIMONIALS MANAGEMENT COMPONENT 2025
 * 
 * Toggle-Sektion für Holistic Testimonial nach Design-Guide:
 * - 1:1 Text-Edit-Design
 * - Dynamische Holistic Testimonials kompakt zusammengefasst
 * - Rating-System und Avatar-URLs
 * - Add/Remove-Funktionalität
 * - Spezifische Klassennamen
 */
function HolisticTestimonialsManagement({
    testimonials,
    addTestimonial,
    removeTestimonial,
    updateTestimonial,
    handleTestimonialAvatarUpload,
    uploadingStates,
    editData
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const ratings = [1, 2, 3, 4, 5];

    return (
        <div className="holistic-testimonials-management">
            {/* Toggle Header */}
            <div 
                className={`holistic-testimonials-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="holistic-testimonials-header-content">
                    <div className="holistic-testimonials-header-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1L10 5H14L11 8L12 12L8 10L4 12L5 8L2 5H6L8 1Z" stroke="currentColor" strokeWidth="1.2"/>
                            <circle cx="6" cy="6" r="0.5" fill="currentColor" opacity="0.8"/>
                            <circle cx="8" cy="6" r="0.5" fill="currentColor"/>
                            <circle cx="10" cy="6" r="0.5" fill="currentColor" opacity="0.8"/>
                            <path d="M5 10h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <h3 className="holistic-testimonials-header-title">Testimonials</h3>
                    <div className="holistic-testimonials-counter">
                        {testimonials?.length || 0}
                    </div>
                </div>
                <div className={`holistic-testimonials-toggle-arrow ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="holistic-testimonials-content">
                    {/* Add Button */}
                    <div className="add-holistic-testimonial-section">
                        <button
                            onClick={addTestimonial}
                            className="add-holistic-testimonial-btn"
                        >
                            <div className="btn-icon">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <span>Add New Testimonial</span>
                        </button>
                        <div className="add-holistic-testimonial-hint">
                            Create holistic testimonials with ratings and detailed customer information
                        </div>
                    </div>

                    {/* Testimonials Grid */}
                    {testimonials && testimonials.length > 0 && (
                        <div className="holistic-testimonials-grid">
                            {testimonials.map((testimonial, index) => (
                                <div key={testimonial.id || index} className="holistic-testimonial-edit-card">
                                    {/* Card Header */}
                                    <div className="holistic-testimonial-card-header">
                                        <div className="holistic-testimonial-card-title">
                                            <div className="holistic-testimonial-card-number">{index + 1}</div>
                                            <span>Holistic Testimonial</span>
                                        </div>
                                        <button
                                            onClick={() => removeTestimonial(index)}
                                            className="holistic-testimonial-remove-btn"
                                            title="Remove testimonial"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Rating Section */}
                                    <div className="holistic-testimonial-rating-section">
                                        <label className="holistic-testimonial-field-label">Rating</label>
                                        <div className="rating-selector">
                                            {ratings.map((rating) => (
                                                <button
                                                    key={rating}
                                                    onClick={() => updateTestimonial(index, 'rating', rating)}
                                                    className={`rating-btn ${testimonial.rating === rating ? 'active' : ''}`}
                                                    title={`${rating} Star${rating > 1 ? 's' : ''}`}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path 
                                                            d="M8 1L10 5H14L11 8L12 12L8 10L4 12L5 8L2 5H6L8 1Z" 
                                                            stroke="currentColor" 
                                                            fill={testimonial.rating >= rating ? 'currentColor' : 'none'}
                                                            strokeWidth="1"
                                                        />
                                                    </svg>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Text Fields */}
                                    <div className="holistic-testimonial-content-section">
                                        <label className="holistic-testimonial-field-label">Testimonial Text</label>
                                        <textarea
                                            value={testimonial.text || ''}
                                            onChange={(e) => updateTestimonial(index, 'text', e.target.value)}
                                            placeholder="Enter holistic testimonial text..."
                                            className="holistic-testimonial-textarea"
                                            rows={4}
                                        />
                                    </div>

                                    <div className="holistic-testimonial-content-section">
                                        <label className="holistic-testimonial-field-label">Author Name</label>
                                        <input
                                            type="text"
                                            value={testimonial.author || ''}
                                            onChange={(e) => updateTestimonial(index, 'author', e.target.value)}
                                            placeholder="Enter author name..."
                                            className="holistic-testimonial-input"
                                        />
                                    </div>

                                    <div className="holistic-testimonial-content-section">
                                        <label className="holistic-testimonial-field-label">Location</label>
                                        <input
                                            type="text"
                                            value={testimonial.location || ''}
                                            onChange={(e) => updateTestimonial(index, 'location', e.target.value)}
                                            placeholder="Enter location..."
                                            className="holistic-testimonial-input"
                                        />
                                    </div>

                                    {/* Avatar Upload Section */}
                                    <div className="holistic-testimonial-avatar-section">
                                        <div className="holistic-testimonial-avatar-preview-container">
                                            <div className="holistic-testimonial-avatar-preview">
                                                {testimonial.avatar ? (
                                                    <img
                                                        src={testimonial.avatar}
                                                        alt={`${testimonial.author || 'Testimonial'} Avatar`}
                                                        className="holistic-testimonial-avatar-img"
                                                    />
                                                ) : (
                                                    <div className="holistic-testimonial-avatar-placeholder">
                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <circle cx="10" cy="7" r="3" stroke="#dee2e6" strokeWidth="1.5"/>
                                                            <path d="M4 17v-1a4 4 0 014-4h4a4 4 0 014 4v1" stroke="#dee2e6" strokeWidth="1.5" strokeLinecap="round"/>
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Avatar Controls */}
                                        <div className="holistic-testimonial-avatar-controls">
                                            <label className="holistic-testimonial-avatar-control-btn upload" title="Upload avatar">
                                                {uploadingStates?.[index] ? (
                                                    <div className="holistic-testimonial-upload-spinner"></div>
                                                ) : (
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                        <path d="M7 1V9M4 4L7 1L10 4M2 9V11C2 12.1 2.9 13 4 13H10C11.1 13 12 12.1 12 11V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        if (e.target.files[0]) {
                                                            handleTestimonialAvatarUpload(index, e.target.files[0]);
                                                        }
                                                    }}
                                                    style={{ display: 'none' }}
                                                />
                                            </label>

                                            {testimonial.avatar && (
                                                <button
                                                    onClick={() => updateTestimonial(index, 'avatar', '')}
                                                    className="holistic-testimonial-avatar-control-btn clear"
                                                    title="Clear avatar"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                        <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {(!testimonials || testimonials.length === 0) && (
                        <div className="holistic-testimonials-empty-state">
                            <div className="holistic-testimonials-empty-state-icon">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" opacity="0.3"/>
                                    <path d="M24 14L26 22H32L27 26L28 34L24 30L20 34L21 26L16 22H22L24 14Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                                    <circle cx="20" cy="20" r="0.5" fill="currentColor" opacity="0.6"/>
                                    <circle cx="24" cy="20" r="0.5" fill="currentColor" opacity="0.8"/>
                                    <circle cx="28" cy="20" r="0.5" fill="currentColor" opacity="0.6"/>
                                    <path d="M18 28h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                                </svg>
                            </div>
                            <h4>No Holistic Testimonials Yet</h4>
                            <p>Click "Add New Testimonial" to create your first holistic customer review</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default HolisticTestimonialsManagement;
